<template>
  <div>
    <b-tabs pills cards>
      <b-tab active>
        <template #title>
          <feather-icon
            icon="BoxIcon"
            size="16"
            class="mr-50"
          />
          <span>All Products{{ allProducts.totalRows !== undefined ? ` (${allProducts.totalRows})` : '' }}</span>
        </template>

        <div v-if="allProducts && allProducts.totalRows !== undefined" class="d-flex py-0 justify-content-start align-items-center mb-3">
          <h5>{{ allProducts.totalRows }} results</h5>
        </div>

        <category-details-distribution
          v-if="allProducts.totalRows > 0"
          :distributions="allDistributions"
          :loading="categoryData.loading"
        />

        <category-product-table
          v-if="allProducts"
          :products="allProducts"
          @row-clicked="handleRowClick"
        />
      </b-tab>

      <b-tab>
        <template #title>
          <feather-icon
            icon="ActivityIcon"
            size="16"
            class="mr-50"
          />
          <span>Live Products{{ liveProducts.totalRows !== undefined ? ` (${liveProducts.totalRows})` : '' }}</span>
        </template>

        <div v-if="liveProducts && liveProducts.totalRows !== undefined" class="d-flex py-0 justify-content-start align-items-center mb-3">
          <h5>{{ liveProducts.totalRows }} results</h5>
        </div>

        <category-details-distribution
          v-if="liveProducts.totalRows > 0"
          :distributions="liveDistributions"
          :loading="categoryData.loading"
        />

        <category-product-table
          v-if="liveProducts"
          :products="liveProducts"
          @row-clicked="handleRowClick"
        />
      </b-tab>

      <b-tab>
        <template #title>
          <feather-icon
            icon="ClockIcon"
            size="16"
            class="mr-50"
          />
          <span>Current Products{{ currentProducts.totalRows !== undefined ? ` (${currentProducts.totalRows})` : '' }}</span>
        </template>

        <div v-if="currentProducts && currentProducts.totalRows !== undefined" class="d-flex py-0 justify-content-start align-items-center mb-3">
          <h5>{{ currentProducts.totalRows }} results</h5>
        </div>

        <category-details-distribution
          v-if="currentProducts.totalRows > 0"
          :distributions="currentDistributions"
          :loading="categoryData.loading"
        />

        <category-product-table
          v-if="currentProducts"
          :products="currentProducts"
          @row-clicked="handleRowClick"
        />
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import {
  BTabs,
  BTab,
} from 'bootstrap-vue'
import CategoryDetailsDistribution from './CategoryDetailsDistribution.vue'
import CategoryProductTable from './CategoryProductTable.vue'

export default {
  components: {
    BTabs,
    BTab,
    CategoryDetailsDistribution,
    CategoryProductTable,
  },
  props: {
    category: {
      type: String,
      required: true,
    },
    categoryId: {
      type: [String, Number],
      required: true,
    },
    filterKey: {
      type: String,
      required: true,
      // e.g., "providers", "underlyings", "productTypes"
    },
    filterValue: {
      type: String,
      required: true,
      // e.g., "UBS", "S&P 500", "Capital Protection"
    },
  },
  data() {
    return {
      baseURL: process.env.VUE_APP_BACKEND_PATH,
      categoryData: {
        current: {
          categories: [],
          data: [],
        },
        live: {
          categories: [],
          data: [],
        },
        all: {
          categories: [],
          data: [],
        },
        loading: false,
        error: false,
      },
      currentProducts: {
        data: [],
        loading: false,
        error: false,
        fields: [
          {
            key: 'productid', label: 'Id', thStyle: { width: '11%' }, sortable: true,
          },
          {
            key: 'producttype', label: 'Type', thStyle: { width: '16%' }, sortable: true,
          },
          {
            key: 'productname', label: 'Name', thStyle: { width: '40%' }, sortable: true,
          },
          {
            key: 'date_1_strike', label: 'Strike Date', thStyle: { width: '13%' }, sortable: true,
          },
          {
            key: 'date_2_finalmarket', label: 'Final Date', thStyle: { width: '13%' }, sortable: true,
          },
        ],
        sortBy: '',
        sortDesc: false,
        search: '',
        currentPage: 1,
        perPage: 20,
        totalRows: 0,
      },
      liveProducts: {
        data: [],
        loading: false,
        error: false,
        fields: [
          {
            key: 'productid', label: 'Id', thStyle: { width: '11%' }, sortable: true,
          },
          {
            key: 'producttype', label: 'Type', thStyle: { width: '16%' }, sortable: true,
          },
          {
            key: 'productname', label: 'Name', thStyle: { width: '40%' }, sortable: true,
          },
          {
            key: 'date_1_strike', label: 'Strike Date', thStyle: { width: '13%' }, sortable: true,
          },
          {
            key: 'date_2_finalmarket', label: 'Final Date', thStyle: { width: '13%' }, sortable: true,
          },
        ],
        sortBy: '',
        sortDesc: false,
        search: '',
        currentPage: 1,
        perPage: 20,
        totalRows: 0,
      },
      allProducts: {
        data: [],
        loading: false,
        error: false,
        fields: [
          {
            key: 'productid', label: 'Id', thStyle: { width: '11%' }, sortable: true,
          },
          {
            key: 'producttype', label: 'Type', thStyle: { width: '16%' }, sortable: true,
          },
          {
            key: 'productname', label: 'Name', thStyle: { width: '40%' }, sortable: true,
          },
          {
            key: 'date_1_strike', label: 'Strike Date', thStyle: { width: '13%' }, sortable: true,
          },
          {
            key: 'date_2_finalmarket', label: 'Final Date', thStyle: { width: '13%' }, sortable: true,
          },
        ],
        sortBy: '',
        sortDesc: false,
        search: '',
        currentPage: 1,
        perPage: 20,
        totalRows: 0,
      },
    }
  },
  computed: {
    currentDistributions() {
      return this.buildDistributions(this.categoryData.current)
    },
    liveDistributions() {
      return this.buildDistributions(this.categoryData.live)
    },
    allDistributions() {
      return this.buildDistributions(this.categoryData.all)
    },
  },
  watch: {
    category() {
      this.getCategoryDataById()
      this.loadCurrentProducts()
      this.loadLiveProducts()
      this.loadAllProducts()
    },
    categoryId() {
      this.getCategoryDataById()
      this.loadCurrentProducts()
      this.loadLiveProducts()
      this.loadAllProducts()
    },
    filterKey() {
      this.loadCurrentProducts()
      this.loadLiveProducts()
      this.loadAllProducts()
    },
    filterValue() {
      this.loadCurrentProducts()
      this.loadLiveProducts()
      this.loadAllProducts()
    },
    'currentProducts.currentPage': function () {
      this.loadCurrentProducts()
    },
    'currentProducts.sortBy': function () {
      this.loadCurrentProducts()
    },
    'currentProducts.sortDesc': function () {
      this.loadCurrentProducts()
    },
    'liveProducts.currentPage': function () {
      this.loadLiveProducts()
    },
    'liveProducts.sortBy': function () {
      this.loadLiveProducts()
    },
    'liveProducts.sortDesc': function () {
      this.loadLiveProducts()
    },
    'allProducts.currentPage': function () {
      this.loadAllProducts()
    },
    'allProducts.sortBy': function () {
      this.loadAllProducts()
    },
    'allProducts.sortDesc': function () {
      this.loadAllProducts()
    },
  },
  mounted() {
    this.getCategoryDataById()
    this.loadCurrentProducts()
    this.loadLiveProducts()
    this.loadAllProducts()
  },
  methods: {
    getCategoryDataById() {
      if (!this.category || !this.categoryId) return

      this.categoryData.loading = true
      this.categoryData.error = false

      this.$http
        .get(`public.cfc?method=getCategoryDataById&category=${this.category}&id=${this.categoryId}`)
        .then(response => {
          console.log('Category Data by ID:', response.data)

          // Process CURRENTREPORTS (current products)
          if (response.data.CURRENTREPORTS) {
            this.categoryData.current.categories = response.data.CURRENTREPORTS.CATEGORY || []
            this.categoryData.current.data = response.data.CURRENTREPORTS.CATEGORYDATA || []
          }

          // Process LIVEREPORTS (live products)
          if (response.data.LIVEREPORTS) {
            this.categoryData.live.categories = response.data.LIVEREPORTS.CATEGORY || []
            this.categoryData.live.data = response.data.LIVEREPORTS.CATEGORYDATA || []
          }

          // Process ALLREPORTS (all products)
          if (response.data.ALLREPORTS) {
            this.categoryData.all.categories = response.data.ALLREPORTS.CATEGORY || []
            this.categoryData.all.data = response.data.ALLREPORTS.CATEGORYDATA || []
          }
        })
        .catch(error => {
          console.error('Error fetching category data by ID:', error)
          this.categoryData.error = error
        })
        .finally(() => {
          this.categoryData.loading = false
        })
    },
    loadCurrentProducts() {
      if (!this.filterValue) return

      this.currentProducts.loading = true
      this.currentProducts.error = false

      const searchFilter = {
        search: '',
        filter: {
          [this.filterKey]: [this.filterValue],
          status: [0],
        },
        constraints: {
          dateFrom: '',
          dateTo: '',
          dateType: 'None',
        },
        sortBy: this.currentProducts.sortBy,
        sortDesc: this.currentProducts.sortDesc,
      }

      this.$http
        .get('lifecycle/public/data.cfc?method=getFilterResults', {
          params: {
            searchFilter,
            perpage: this.currentProducts.perPage,
            page: this.currentProducts.currentPage,
          },
        })
        .then(response => {
          this.currentProducts.data = response.data.DATA
          this.currentProducts.totalRows = response.data.INFO.NUMROWS
          this.currentProducts.currentPage = response.data.INFO.CURRENTPAGE * 1
        })
        .catch(error => {
          this.currentProducts.error = error
        })
        .finally(() => {
          this.currentProducts.loading = false
        })
    },
    loadLiveProducts() {
      if (!this.filterValue) return

      this.liveProducts.loading = true
      this.liveProducts.error = false

      const searchFilter = {
        search: '',
        filter: {
          [this.filterKey]: [this.filterValue],
          status: [1],
        },
        constraints: {
          dateFrom: '',
          dateTo: '',
          dateType: 'None',
        },
        sortBy: this.liveProducts.sortBy,
        sortDesc: this.liveProducts.sortDesc,
      }

      this.$http
        .get('lifecycle/public/data.cfc?method=getFilterResults', {
          params: {
            searchFilter,
            perpage: this.liveProducts.perPage,
            page: this.liveProducts.currentPage,
          },
        })
        .then(response => {
          this.liveProducts.data = response.data.DATA
          this.liveProducts.totalRows = response.data.INFO.NUMROWS
          this.liveProducts.currentPage = response.data.INFO.CURRENTPAGE * 1
        })
        .catch(error => {
          this.liveProducts.error = error
        })
        .finally(() => {
          this.liveProducts.loading = false
        })
    },
    loadAllProducts() {
      if (!this.filterValue) return

      this.allProducts.loading = true
      this.allProducts.error = false

      const searchFilter = {
        search: '',
        filter: {
          [this.filterKey]: [this.filterValue],
        },
        constraints: {
          dateFrom: '',
          dateTo: '',
          dateType: 'None',
        },
        sortBy: this.allProducts.sortBy,
        sortDesc: this.allProducts.sortDesc,
      }

      this.$http
        .get('lifecycle/public/data.cfc?method=getFilterResults', {
          params: {
            searchFilter,
            perpage: this.allProducts.perPage,
            page: this.allProducts.currentPage,
          },
        })
        .then(response => {
          this.allProducts.data = response.data.DATA
          this.allProducts.totalRows = response.data.INFO.NUMROWS
          this.allProducts.currentPage = response.data.INFO.CURRENTPAGE * 1
        })
        .catch(error => {
          this.allProducts.error = error
        })
        .finally(() => {
          this.allProducts.loading = false
        })
    },
    buildDistributions(categoryDataObj) {
      if (!categoryDataObj.categories || !categoryDataObj.data) return []

      return categoryDataObj.categories.map((category, index) => {
        const categoryLower = category.toLowerCase()
        const hideLogos = categoryLower !== 'product type' && categoryLower !== 'provider'
        const useIcons = categoryLower !== 'provider'

        return {
          title: category,
          subtitle: `Distribution by ${categoryLower}`,
          data: this.transformCategoryData(categoryDataObj.data[index], categoryLower),
          hideLogos,
          useIcons,
        }
      })
    },
    transformCategoryData(categoryObj, categoryType) {
      if (!categoryObj || !categoryObj.CONTENT) return []

      return categoryObj.CONTENT.map(item => {
        const transformed = {
          name: item.category,
          numberOfProducts: item.number,
          id: item.thisid,
        }

        // If this is a provider category, add logoUrl
        if (categoryType === 'provider' && item.thisid) {
          transformed.logoUrl = `${this.baseURL}serveproviderlogo.cfm?id=${item.thisid}`
        }

        // If this is a product type category, add icon properties
        if (categoryType === 'product type') {
          transformed.iconname = item.pticonname || null
          transformed.iconcolor = item.pticoncolor || null
        }

        return transformed
      })
    },
    handleRowClick(item) {
      this.$router.push({
        name: 'product-report',
        params: {
          reportNumber: item.productid,
        },
      })
    },
  },
}
</script>

<style>
/* Hover effect for pill tabs */
.nav-pills .nav-link:not(.active):hover {
  background-color: rgba(26, 115, 232, 0.12);
  transition: all 0.2s ease;
}

.nav-pills .nav-link {
  transition: all 0.2s ease;
}

/* Dark mode hover effect */
.dark-layout .nav-pills .nav-link:not(.active):hover {
  background-color: rgba(26, 115, 232, 0.2);
}
</style>
